/**
 * A stage vocabulary, as a board reads it.
 *
 * `/api/lead-stages/catalog?vocabulary=…` answers with the tenant's stages and
 * the moves declared between them, and every board in this product asks the
 * same four questions of that answer: which columns do I draw, what do I print
 * above each, which id do I send to move a card, and where may this card go.
 * Those four are the whole of what the lead board and the roll-off board have
 * in common, so they live here and nothing else does — the two boards differ
 * in almost everything else, and a shared component carrying a flag for each
 * difference would be worse than two honest ones.
 *
 * ── CODE, NAME, OUTCOME ───────────────────────────────────────────────────
 *
 * A stage crosses the wire as three things and they are not interchangeable:
 *
 *     code     the identity           compare, group and colour by this
 *     name     the tenant's wording   print this, and never branch on it
 *     outcome  open | won | lost      decide "is it finished" by this
 *
 * The functions below take `code` everywhere a stage is named and return
 * `code` everywhere they name one back, so a caller that stays inside them
 * cannot accidentally compare against a label. `stageLabelOf` is the one place
 * a name is produced, and it is the last step before the screen.
 */

/** One stage, exactly as `LeadStageDto` sends it. */
export interface StageCatalogEntry {
  id: number;
  /** The tenant's wording for this stage. Display only. */
  name: string;
  /** The stage's identity. Every comparison in this product uses it. */
  code: string;
  /** `open` | `won` | `lost` — stated by the stage, not guessed from `name`. */
  outcome: string;
  /** `lead` | `rolloff` — which set of stages this belongs to. */
  vocabulary: string;
  description: string | null;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
}

/** One declared move, as `LeadStageTransitionDto` sends it: two stage ids. */
export interface StageTransition {
  fromLeadStageId: number;
  toLeadStageId: number;
}

/** The catalog endpoint's whole answer. */
export interface StageCatalogResponse {
  stages: StageCatalogEntry[];
  transitions: StageTransition[];
}

/**
 * The columns a board draws: the tenant's ACTIVE stages, in the order the
 * tenant put them in.
 *
 * Inactive stages are dropped because a board offers its columns as drop
 * targets and the server refuses a move into one (`ChangeStageAsync` checks
 * `IsActive` before anything else). A card still sitting in a stage that was
 * since retired keeps its own name and colour — that is `stageLabelOf`'s job,
 * and it deliberately does not filter.
 *
 * The id breaks a `sortOrder` tie, matching how the server picks the front of
 * a branch, so two stages sharing a position order the same way on both ends.
 */
export function orderedStages(
  stages: readonly StageCatalogEntry[],
): StageCatalogEntry[] {
  return stages
    .filter(stage => stage.isActive)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}

/**
 * The tenant's wording for a code.
 *
 * A code with no stage behind it prints as itself rather than blank: an old
 * link, or a card left in a retired stage, is better answered visibly than
 * silently. Retired stages are searched too — a header may be gone from the
 * board while a history row still names it.
 */
export function stageLabelOf(
  stages: readonly StageCatalogEntry[],
  code: string | null | undefined,
): string {
  if (!code) return '';
  return stages.find(stage => stage.code === code)?.name ?? code;
}

/**
 * The id to send when moving a card into this stage.
 *
 * By code, never by name: a tenant may add stages of its own and rename the
 * ones it was given, so the wording is not an identity however much it looks
 * like one.
 */
export function stageIdOf(
  stages: readonly StageCatalogEntry[],
  code: string | null | undefined,
): number | null {
  if (!code) return null;
  return stages.find(stage => stage.code === code)?.id ?? null;
}

/** Whether a stage ends the pursuit, asked of the stage rather than its name. */
export function stageOutcomeOf(
  stages: readonly StageCatalogEntry[],
  code: string | null | undefined,
): string | null {
  if (!code) return null;
  return stages.find(stage => stage.code === code)?.outcome ?? null;
}

/**
 * WHERE THE RULE LIVES
 *
 * "Which stages may this card be dropped on" is NOT here. It is
 * `legalMoveCodes` in `pages/leads/board/stage-moves.ts`, and both boards call that
 * one. This file holds only the mechanical readings of a catalog - order,
 * wording, id - which have no policy in them.
 *
 * That split was not free: this file briefly carried its own copy of the rule,
 * written at the same time as the other one and to the same specification.
 * Two correct copies of a rule are still two copies, and the whole series of
 * changes this belongs to exists because of what happens when they drift.
 */
